"use client";

import { Button } from "@package/subframe/components/Button";
import { FeatherUserPlus } from "@subframe/core";
import { useState } from "react";

import { InviteMemberModal } from "@/components/InviteMemberModal";
import { Pagination } from "@/components/ui/pagination";
import { useListInvitationsQueryControllerList } from "@/generated/invitations/invitations";
import { useListMembersQueryControllerListMembers } from "@/generated/membership/membership";
import { useFindOrganisationQueryControllerFindBySlug } from "@/generated/organisations/organisations";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

import { InvitationsTab } from "./InvitationsTab";
import { MembersTab } from "./MembersTab";

interface MembersTableProps {
  organisationSlug: string;
}

export function MembersTable({ organisationSlug }: MembersTableProps) {
  const [activeTab, setActiveTab] = useState<"members" | "invitations">(
    "members",
  );
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [membersPage, setMembersPage] = useState(1);
  const [membersPageSize, setMembersPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [invitationsPage, setInvitationsPage] = useState(1);
  const [invitationsPageSize, setInvitationsPageSize] =
    useState(DEFAULT_PAGE_SIZE);

  const { data: organisationData } =
    useFindOrganisationQueryControllerFindBySlug(organisationSlug);

  const {
    data: membersData,
    isLoading: membersLoading,
    error: membersError,
  } = useListMembersQueryControllerListMembers(organisationSlug, {
    limit: membersPageSize,
    offset: (membersPage - 1) * membersPageSize,
  });

  const {
    data: invitationsData,
    isLoading: invitationsLoading,
    error: invitationsError,
  } = useListInvitationsQueryControllerList(organisationSlug, {
    limit: invitationsPageSize,
    offset: (invitationsPage - 1) * invitationsPageSize,
  });

  const membersCount = membersData?.data.total || 0;
  const invitationsCount = invitationsData?.data.total || 0;

  return (
    <div className="flex w-full flex-col items-start gap-6 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Team Members - {organisationData?.data.name}
            </span>
            <span className="text-body font-body text-subtext-color">
              Manage nursery group members and invitations
            </span>
          </div>
          <Button
            variant="brand-primary"
            icon={<FeatherUserPlus />}
            onClick={() => setInviteModalOpen(true)}
          >
            Invite Member
          </Button>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex w-full items-center gap-2 border-b border-solid border-secondary-beige-200">
        <button
          type="button"
          onClick={() => setActiveTab("members")}
          className={`px-4 py-2 text-body font-body transition-colors border-b-2 -mb-[1px] ${
            activeTab === "members"
              ? "border-primary-navy-600 text-primary-navy-600"
              : "border-transparent text-subtext-color hover:text-default-font"
          }`}
        >
          Members ({membersCount})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("invitations")}
          className={`px-4 py-2 text-body font-body transition-colors border-b-2 -mb-[1px] ${
            activeTab === "invitations"
              ? "border-primary-navy-600 text-primary-navy-600"
              : "border-transparent text-subtext-color hover:text-default-font"
          }`}
        >
          Invitations ({invitationsCount})
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex w-full flex-col items-start">
        {activeTab === "members" && (
          <>
            <MembersTab
              data={membersData?.data.data || []}
              isLoading={membersLoading}
              error={membersError}
            />
            {!membersLoading && !membersError && (
              <Pagination
                className="pt-4"
                page={membersPage}
                pageSize={membersPageSize}
                totalCount={membersCount}
                onPageChange={setMembersPage}
                onPageSizeChange={(size) => {
                  setMembersPageSize(size);
                  setMembersPage(1);
                }}
                itemNoun="members"
              />
            )}
          </>
        )}
        {activeTab === "invitations" && (
          <>
            <InvitationsTab
              organisationSlug={organisationSlug}
              data={invitationsData?.data.data || []}
              isLoading={invitationsLoading}
              error={invitationsError}
            />
            {!invitationsLoading && !invitationsError && (
              <Pagination
                className="pt-4"
                page={invitationsPage}
                pageSize={invitationsPageSize}
                totalCount={invitationsCount}
                onPageChange={setInvitationsPage}
                onPageSizeChange={(size) => {
                  setInvitationsPageSize(size);
                  setInvitationsPage(1);
                }}
                itemNoun="invitations"
              />
            )}
          </>
        )}
      </div>

      {/* Invite Modal */}
      <InviteMemberModal
        organisationSlug={organisationSlug}
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
      />
    </div>
  );
}
